import { useState, useEffect, useRef } from "react";
import { getSession } from "../service/auth";
import {
  getAttendanceRecords,
  getOpenAttendance,
  checkIn,
  checkOut,
} from "../service/rpc";
import { useUser } from "../context/UserContext";
import Modal from "../components/Modal";
import "../styles/Attendance.css";

/* ── helpers ── */
const pad = (n) => String(n).padStart(2, "0");

const formatDatetime = (dt) => {
  if (!dt) return "—";
  try {
    const d = new Date(dt.replace(" ", "T") + "Z");
    return d.toLocaleString([], {
      year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit", second: "2-digit",
    });
  } catch { return dt; }
};

const formatTime = (dt) => {
  if (!dt) return "—";
  try {
    const d = new Date(dt.replace(" ", "T") + "Z");
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false });
  } catch { return dt; }
};

const PAGE_SIZE = 20;

export default function AttendancePage() {
  const session = getSession();
  const [records, setRecords] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [openRecord, setOpenRecord] = useState(null);
  const [liveSecs, setLiveSecs] = useState(0);
  const [viewType, setViewType] = useState("list");
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const { employeeId, loading: userLoading } = useUser();

  const [inModalOpen, setInModalOpen] = useState(false);
  const [outModalOpen, setOutModalOpen] = useState(false);
  const [attType, setAttType] = useState("online_meeting");
  const [checkinNote, setCheckinNote] = useState("");
  const [checkoutNote, setCheckoutNote] = useState("");

  /* ── Responsive Logic ── */
  useEffect(() => {
    if (window.innerWidth <= 992) {
      setViewType("grid");
    }
  }, []);

  /* ── Core data loader ── */
  const loadData = async (pg = 0) => {
    if (!session?.uid) return;
    setLoading(true);
    try {
      const domain = [];

      const [{ records: recs, count }, open] = await Promise.all([
        getAttendanceRecords(domain, PAGE_SIZE, pg * PAGE_SIZE),
        getOpenAttendance(session.uid),
      ]);

      setRecords(recs);
      setTotal(count);
      setOpenRecord(open || null);

      if (open) {
        const elapsed = Math.floor(
          (Date.now() - new Date(open.checkin.replace(" ", "T") + "Z").getTime()) / 1000
        );
        setLiveSecs(Math.max(0, elapsed));
      } else {
        setLiveSecs(0);
      }
    } catch (e) {
      console.error("AttendancePage loadData:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (session?.uid) {
      loadData(0);
    }
  }, [session?.uid]);

  const prevPage = useRef(null);
  useEffect(() => {
    if (prevPage.current !== null && prevPage.current !== page) loadData(page);
    prevPage.current = page;
  }, [page]);

  useEffect(() => {
    let iv;
    if (openRecord) iv = setInterval(() => setLiveSecs((s) => s + 1), 1000);
    return () => clearInterval(iv);
  }, [openRecord]);

  const handleCheckIn = async () => {
    if (!session?.uid || actionLoading) return;
    setActionLoading(true);
    try {
      await checkIn(employeeId, session.uid, attType, checkinNote);
      setInModalOpen(false);
      setCheckinNote("");
      setPage(0);
      await loadData(0);
    }
    catch (e) { alert("Check-in failed: " + e.message); }
    finally { setActionLoading(false); }
  };

  const handleCheckOut = async () => {
    if (!openRecord || actionLoading) return;
    setActionLoading(true);
    try {
      await checkOut(openRecord.id, checkoutNote);
      setOutModalOpen(false);
      setCheckoutNote("");
      setPage(0);
      await loadData(0);
    }
    catch (e) { alert("Check-out failed: " + e.message); }
    finally { setActionLoading(false); }
  };

  const isCheckedIn = !!openRecord;
  const totalPages = Math.ceil(total / PAGE_SIZE);

  const checkInTimeLabel = openRecord
    ? new Date(openRecord.checkin.replace(" ", "T") + "Z")
      .toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    : null;

  const liveFormatted = `${pad(Math.floor(liveSecs / 3600))}:${pad(Math.floor((liveSecs % 3600) / 60))}:${pad(liveSecs % 60)}`;

  return (
    <div className="att-wrap">

      {/* ── Page Header ── */}
      <div className="att-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
          <h1 className="att-header__title">Attendance</h1>
          <div className="view-toggle">
            <button className={`view-toggle__btn ${viewType === 'list' ? 'view-toggle__btn--active' : ''}`} onClick={() => setViewType('list')}>
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="8" y1="6" x2="21" y2="6" /><line x1="8" y1="12" x2="21" y2="12" /><line x1="8" y1="18" x2="21" y2="18" /><line x1="3" y1="6" x2="3.01" y2="6" /><line x1="3" y1="12" x2="3.01" y2="12" /><line x1="3" y1="18" x2="3.01" y2="18" />
              </svg>
              List
            </button>
            <button className={`view-toggle__btn ${viewType === 'grid' ? 'view-toggle__btn--active' : ''}`} onClick={() => setViewType('grid')}>
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" /><rect x="14" y="14" width="7" height="7" /><rect x="3" y="14" width="7" height="7" />
              </svg>
              Kanban
            </button>
          </div>
        </div>

        {userLoading ? (
          <div className="att-status" style={{ borderColor: '#f5d88a', background: 'linear-gradient(135deg, #fef9ec 0%, #fef2d0 100%)', padding: '14px 20px', height: '70px', border: '1.5px solid #f5d88a' }}>
            {/* <span className="att-status__label">Loading profile...</span> */}
          </div>
        ) : session?.uid ? (
          <div className={`att-status ${isCheckedIn ? "att-status--in" : "att-status--out"}`} style={{ margin: 0 }}>
            {isCheckedIn ? (
              <>
                <div className="att-status__dot att-status__dot--in" />
                <div className="att-status__info">
                  <span className="att-status__label">In {checkInTimeLabel}</span>
                  <span className="att-status__timer">{liveFormatted}</span>
                </div>
                <button className="att-action-btn att-action-btn--out" onClick={() => setOutModalOpen(true)} disabled={actionLoading}>
                  {actionLoading ? <span className="att-spinner" /> : (
                    <><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><polyline points="16 17 21 12 16 7" /> <line x1="21" y1="12" x2="9" y2="12" /></svg>Check out</>
                  )}
                </button>
              </>
            ) : (
              <>
                <div className="att-status__dot att-status__dot--out" />
                <div className="att-status__info">
                  <span className="att-status__label">Out</span>
                  <span className="att-status__timer">00:00:00</span>
                </div>
                <button className="att-action-btn att-action-btn--in" onClick={() => setInModalOpen(true)} disabled={actionLoading || loading}>
                  {actionLoading ? <span className="att-spinner" /> : (
                    <><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" /><polyline points="10 17 15 12 10 7" /><line x1="15" y1="12" x2="3" y2="12" /></svg>Check in</>
                  )}
                </button>
              </>
            )}
          </div>
        ) : null}
      </div>

      {/* ── Records Container ── */}
      <div className={`att-table-card ${loading ? "table-card--loading" : ""}`}>
        {loading && (
          <div className="list-loading-overlay">
            <div className="loader"></div>
            <p style={{ color: "#64748b", fontWeight: "500", fontFamily: "Sora" }}>Loading...</p>
          </div>
        )}

        {viewType === "list" ? (
          <div className="att-table-scroll">
            <table className="att-table">
              <thead>
                <tr>
                  <th className="att-th" width="120px">Check In</th>
                  <th className="att-th" width="150px">Type</th>
                  <th className="att-th">In Note</th>
                  <th className="att-th">Check Out</th>
                  <th className="att-th">Out Note</th>
                </tr>
              </thead>
              <tbody>
                {!loading && records.length === 0 ? (
                  <tr><td colSpan={5} className="att-empty">No attendance records found.</td></tr>
                ) : records.map((rec) => {
                  const isOpen = !rec.checkout;
                  return (
                    <tr
                      key={rec.id}
                      className={`att-row ${isOpen ? "att-row--open" : ""}`}
                    >
                      <td className={`att-td ${isOpen ? "att-td--highlight" : ""}`}>{formatDatetime(rec.checkin)}</td>
                      <td className="att-td">
                        <span className="att-mode-badge" style={{ background: '#eef2ff', color: '#4f46e5', textTransform: 'capitalize' }}>
                          {Array.isArray(rec.type) ? rec.type[1] : (rec.type || '—').replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td className="att-td" title={rec.checkin_note || undefined}>
                        {rec.checkin_note && rec.checkin_note !== false ? rec.checkin_note : "—"}
                      </td>
                      <td className={`att-td ${isOpen ? "att-td--highlight" : ""}`}>
                        {isOpen
                          ? <span className="att-live-badge"><span className="att-live-dot" />Live — {liveFormatted}</span>
                          : formatDatetime(rec.checkout)}
                      </td>
                      <td className="att-td" title={rec.checkout_note || undefined}>
                        {rec.checkout_note && rec.checkout_note !== false ? rec.checkout_note : "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="task-grid">
            {!loading && records.length === 0 ? (
              <div className="att-empty" style={{ padding: '40px' }}>No attendance records found.</div>
            ) : records.map((rec) => {
              const isOpen = !rec.checkout;
              const checkInDate = rec.checkin ? rec.checkin.split(' ')[0] : '—';
              const checkInTime = formatTime(rec.checkin);
              const checkOutTime = formatTime(rec.checkout);

              return (
                <div key={rec.id} className={`kanban-card ${isOpen ? "kanban-card--active" : ""}`}>
                  <div className="att-card__date-label" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '12px', borderBottom: '1px solid rgba(0,0,0,0.05)', marginBottom: '12px' }}>
                    <span style={{ fontWeight: '700', color: '#1a2332' }}>{checkInDate}</span>
                    <span style={{ fontSize: '10px', background: '#eef2ff', padding: '4px 10px', borderRadius: '6px', color: '#4f46e5', fontWeight: '700', textTransform: 'capitalize', border: '1px solid #eef2ff' }}>
                      {Array.isArray(rec.type) ? rec.type[1] : (rec.type || '—').replace(/_/g, ' ')}
                    </span>
                  </div>

                  <div className="att-card__details" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    <div className="att-card__field">
                      <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', color: '#94a3b8', marginBottom: '4px' }}>Check In @ {checkInTime}</label>
                      <div style={{ fontSize: '14px', color: '#475569', lineHeight: '1.5', background: 'rgba(255,255,255,0.5)', padding: '8px', borderRadius: '6px' }}>
                        {rec.checkin_note && rec.checkin_note !== false ? rec.checkin_note : <span style={{ fontStyle: 'italic', opacity: 0.5 }}>No in-note</span>}
                      </div>
                    </div>

                    <div className="att-card__field">
                      <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', color: '#94a3b8', marginBottom: '4px' }}>
                        Check Out {isOpen ? '(Active)' : `@ ${checkOutTime}`}
                      </label>
                      <div style={{whiteSpace:'pre-wrap', wordBreak:'break-word', fontSize: '14px', color: '#475569', lineHeight: '1.5', background: 'rgba(255,255,255,0.5)', padding: '8px', borderRadius: '6px' }}>
                        {isOpen ? (
                          <span className="att-live-badge" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', color: '#10b981', fontWeight: '700' }}>
                            <span className="att-live-dot" /> Live: {liveFormatted}
                          </span>
                        ) : (
                          rec.checkout_note && rec.checkout_note !== false ? rec.checkout_note : <span style={{ fontStyle: 'italic', opacity: 0.5 }}>No out-note</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {totalPages > 1 && !loading && (
        <div className="att-pagination">
          <button className="att-page-btn" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>← Prev</button>
          <span className="att-page-info">Page {page + 1} of {totalPages} &nbsp;·&nbsp; {total} records</span>
          <button className="att-page-btn" disabled={page >= totalPages - 1} onClick={() => setPage((p) => p + 1)}>Next →</button>
        </div>
      )}

      {/* ── Check-In Modal ── */}
      <Modal
        isOpen={inModalOpen}
        onClose={() => setInModalOpen(false)}
        title="Check-In Questionnaire"
        saveLabel="Confirm Check-In"
        onSave={handleCheckIn}
        saving={actionLoading}
      >
        <div className="modal-form__group">
          <label className="modal-form__label">Attendance Type</label>
          <select
            className="modal-form__input"
            value={attType}
            onChange={(e) => setAttType(e.target.value)}
          >
            <option value="check_in">Work Check In</option>
            <option value="check_out">Work Check Out</option>
            <option value="online_meeting">Online Meeting</option>
            <option value="short_break">Short Break</option>
            <option value="lunch_break">Lunch Break</option>
            <option value="onsite_meeting">On-site Meeting</option>
          </select>
        </div>
        <div className="modal-form__group">
          <label className="modal-form__label">Check-In Note</label>
          <textarea
            className="modal-form__input"
            rows={3}
            placeholder="Add a note (optional)..."
            value={checkinNote}
            onChange={(e) => setCheckinNote(e.target.value)}
          />
        </div>
      </Modal>

      {/* ── Check-Out Modal ── */}
      <Modal
        isOpen={outModalOpen}
        onClose={() => setOutModalOpen(false)}
        title="Check-Out Questionnaire"
        saveLabel="Confirm Check-Out"
        onSave={handleCheckOut}
        saving={actionLoading}
      >
        <div className="modal-form__group">
          <label className="modal-form__label">Check-Out Note</label>
          <textarea
            className="modal-form__input"
            rows={3}
            placeholder="What was accomplished? (optional)..."
            value={checkoutNote}
            onChange={(e) => setCheckoutNote(e.target.value)}
          />
        </div>
      </Modal>
    </div>
  );
}